import { Helmet } from "react-helmet-async";

export default function FitterAIPrivacy() {
  return (
    <div className="min-h-screen bg-[#FBFAF6] text-[#26313B] p-8 md:p-16 transition-colors duration-300">
      <Helmet>
        <title>Privacy Policy – FitterAI</title>
        <meta
          name="description"
          content="Privacy Policy for FitterAI, the AI-powered workout planner and fitness coach by Cosmovex."
        />
        <link
          rel="canonical"
          href="https://cosmovexapps.com/apps/fitterai/privacy-policy"
        />
      </Helmet>

      <h1 className="text-4xl font-bold mb-6 text-[#A8720E]">
        Privacy Policy – FitterAI
      </h1>

      <p className="text-[#3D4B59] leading-relaxed mb-6 max-w-4xl">
        <strong>FitterAI</strong> is an AI-powered workout planner and fitness
        coach created by <strong>Cosmovex</strong>. This Privacy Policy explains
        what information FitterAI may collect and process, how it is used, where
        it is stored, and what controls you have when using the app.
      </p>

      <h2 className="text-2xl font-semibold mt-10 mb-4">
        1. Information We Collect
      </h2>

      <ul className="list-disc list-inside text-[#3D4B59] leading-relaxed space-y-2 max-w-4xl">
        <li>
          <strong>Account data:</strong> when you sign in with Google or Apple,
          we process your name, email address, profile photo URL, and
          authentication identifiers via Firebase Authentication.
        </li>
        <li>
          <strong>Fitness profile:</strong> your fitness goal, experience level,
          available equipment, training days, body weight, and preferences you
          provide during onboarding.
        </li>
        <li>
          <strong>Workout data:</strong> workout plans generated by AI, logged
          sets, reps, weights, workout duration, personal records, and streak
          information stored in Cloud Firestore.
        </li>
        <li>
          <strong>Nutrition data:</strong> calorie and macro tracking entries you
          choose to log.
        </li>
        <li>
          <strong>Progress photos:</strong> photos you voluntarily upload for
          progress tracking, stored securely in your account.
        </li>
        <li>
          <strong>AI interactions:</strong> workout plan requests, form analysis
          video frames, coach chat messages, and body prediction photos are sent
          to Google&apos;s Gemini AI servers for processing. These inputs are used
          solely to generate your personalized responses and are not used to train
          AI models.
        </li>
        <li>
          <strong>Subscription data:</strong> purchase identifiers, subscription
          status, platform (iOS/Android), and expiration dates for managing your
          FitterAI Pro subscription.
        </li>
        <li>
          <strong>Diagnostics and usage:</strong> crash reports via Firebase
          Crashlytics and usage events via Firebase Analytics, including screen
          views, feature usage, workout completions, and app version. Analytics
          is disabled in debug builds.
        </li>
        <li>
          <strong>Advertising data:</strong> if you use the free tier, Google
          AdMob may process device identifiers and ad interaction data for
          serving and measuring advertisements.
        </li>
        <li>
          <strong>Push notifications:</strong> Firebase Cloud Messaging tokens
          for delivering workout reminders, streak alerts, and progress updates.
        </li>
      </ul>

      <h2 className="text-2xl font-semibold mt-10 mb-4">
        2. How We Use Information
      </h2>

      <ul className="list-disc list-inside text-[#3D4B59] leading-relaxed space-y-2 max-w-4xl">
        <li>To generate personalized AI workout plans tailored to your goals and progress.</li>
        <li>To provide AI form analysis, coaching chat, and body transformation predictions.</li>
        <li>To track your workout history, streaks, personal records, and fitness progress.</li>
        <li>To deliver voice coaching during workouts based on your exercise data.</li>
        <li>To authenticate you and sync data across devices.</li>
        <li>To manage your FitterAI Pro subscription and restore purchases.</li>
        <li>To send relevant push notifications (workout reminders, streak alerts, progress summaries).</li>
        <li>To serve ads in the free tier and provide an ad-free experience for Pro users.</li>
        <li>To improve app stability, performance, and features through anonymized analytics.</li>
      </ul>

      <h2 className="text-2xl font-semibold mt-10 mb-4">
        3. AI Data Processing
      </h2>

      <p className="text-[#3D4B59] leading-relaxed mb-6 max-w-4xl">
        FitterAI uses Google&apos;s Gemini AI to generate workout plans, analyze
        exercise form, provide coaching advice, and predict body transformations.
        When you use these features, relevant data (your profile, workout history,
        video frames, or photos) is sent to Google&apos;s servers for processing.
        This data is used solely to generate your response and is subject to
        Google&apos;s AI data processing policies. AI-generated workout plans and
        advice are not a substitute for professional medical advice.
      </p>

      <h2 className="text-2xl font-semibold mt-10 mb-4">
        4. Local Storage
      </h2>

      <p className="text-[#3D4B59] leading-relaxed mb-6 max-w-4xl">
        FitterAI caches workout plans, exercise data, user preferences, streak
        information, and onboarding state locally on your device using Hive
        encrypted storage. This enables offline functionality and faster app
        performance. Local data remains on your device until you clear app data
        or uninstall the app.
      </p>

      <h2 className="text-2xl font-semibold mt-10 mb-4">5. Advertising</h2>

      <p className="text-[#3D4B59] leading-relaxed mb-6 max-w-4xl">
        Free users may see ads served through Google AdMob, including banner ads,
        interstitial ads, and rewarded video ads. AdMob may process device
        identifiers, approximate location, and interaction data in accordance
        with Google&apos;s advertising policies. FitterAI Pro users enjoy an
        ad-free experience. Ad formats can be remotely enabled or disabled via
        Firebase Remote Config.
      </p>

      <h2 className="text-2xl font-semibold mt-10 mb-4">6. Data Sharing</h2>

      <p className="text-[#3D4B59] leading-relaxed mb-6 max-w-4xl">
        We rely on service providers including Google Firebase (Authentication,
        Firestore, Analytics, Crashlytics, Cloud Messaging, Remote Config),
        Google Gemini AI, Google AdMob, Google Play, and Apple to provide core
        app functionality. We do not sell your personal data to third parties.
        Information may be disclosed when required by law or to protect our legal
        rights.
      </p>

      <h2 className="text-2xl font-semibold mt-10 mb-4">7. Data Retention</h2>

      <p className="text-[#3D4B59] leading-relaxed mb-6 max-w-4xl">
        Your fitness data is retained in Cloud Firestore as long as your account
        is active. Local device data remains until you delete it or uninstall the
        app. Analytics and crash data are retained according to Firebase&apos;s
        default retention policies. You can request account deletion at any time.
      </p>

      <h2 className="text-2xl font-semibold mt-10 mb-4">
        8. Account Deletion
      </h2>

      <p className="text-[#3D4B59] leading-relaxed mb-6 max-w-4xl">
        You can delete your account and all associated data through the app
        (Settings → Account → Delete Account) or by contacting us. For full
        details, visit our{" "}
        <a
          href="https://cosmovexapps.com/apps/fitterai/account-deletion"
          className="text-[#0F6BBF] underline hover:text-[#0C5AA3]"
        >
          account deletion page
        </a>
        .
      </p>

      <h2 className="text-2xl font-semibold mt-10 mb-4">9. Children&apos;s Privacy</h2>

      <p className="text-[#3D4B59] leading-relaxed mb-6 max-w-4xl">
        FitterAI is not directed to children under 13, and we do not knowingly
        collect personal data from children.
      </p>

      <h2 className="text-2xl font-semibold mt-10 mb-4">10. Changes to This Policy</h2>

      <p className="text-[#3D4B59] leading-relaxed mb-6 max-w-4xl">
        We may update this Privacy Policy from time to time. Material changes
        will be reflected by updating this page and the revision date below.
      </p>

      <h2 className="text-2xl font-semibold mt-10 mb-4">11. Contact</h2>

      <p className="text-[#3D4B59] leading-relaxed max-w-4xl">
        For privacy or data questions related to FitterAI, contact{" "}
        <a
          href="mailto:support@cosmovex.com"
          className="text-[#0F6BBF] underline hover:text-[#0C5AA3]"
        >
          support@cosmovex.com
        </a>
        .
      </p>

      <p className="text-[#8A93A2] mt-10">
        Last updated: March 19, 2026
      </p>
    </div>
  );
}
